export const landingPageViewEventName = "public_site.page.viewed.v1" as const;
export const landingAnalyticsConsentCookie = "dronejob_analytics_consent" as const;
export type LandingAnalyticsConsentState = "granted" | "denied" | "unset";

export const landingAnalyticsEnvironments = [
  "development",
  "staging",
  "production",
] as const;

export type LandingAnalyticsEnvironment = (typeof landingAnalyticsEnvironments)[number];
export type LandingAnalyticsLocale = "en" | "es" | "pt-BR" | "not_applicable";

export type LandingPageViewDimensions = Readonly<{
  routeKey: string;
  locale: LandingAnalyticsLocale;
}>;

export type LandingProductAnalyticsConfig = Readonly<{
  environment: LandingAnalyticsEnvironment;
  host: "https://us.i.posthog.com" | "https://eu.i.posthog.com";
  projectToken: string;
  deploySha: string;
}>;

type PublicConfigInput = Readonly<{
  enabled?: string;
  environment?: string;
  host?: string;
  projectToken?: string;
  deploySha?: string;
}>;

const approvedHosts = new Set<LandingProductAnalyticsConfig["host"]>([
  "https://us.i.posthog.com",
  "https://eu.i.posthog.com",
]);
const landingAnalyticsEnvironmentSet = new Set<string>(landingAnalyticsEnvironments);
const projectTokenPattern = /^phc_[A-Za-z0-9_-]{16,200}$/;
const deployShaPattern = /^(?!0{40}$)[a-f0-9]{40}$/;
const localizedRoutes = new Set([
  "enterprise",
  "help",
  "login",
  "memberships",
  "missions",
  "partners",
  "pilots",
  "platform",
  "register",
  "resources",
  "signup",
  "solutions",
]);
const legalRoutes = new Set(["acceptable-use", "cookie-policy", "privacy", "terms"]);

export function resolveLandingProductAnalyticsConfig(
  input: PublicConfigInput,
): LandingProductAnalyticsConfig | null {
  if (input.enabled !== "true") return null;
  const environment = input.environment?.trim().toLowerCase();
  if (environment !== "development" && environment !== "staging") return null;
  const host = input.host?.trim().replace(/\/$/, "") as LandingProductAnalyticsConfig["host"] | undefined;
  if (!host || !approvedHosts.has(host)) return null;
  const projectToken = input.projectToken?.trim();
  if (!projectToken || !projectTokenPattern.test(projectToken)) return null;
  const deploySha = input.deploySha?.trim().toLowerCase();
  if (!deploySha || !deployShaPattern.test(deploySha)) return null;
  return { environment, host, projectToken, deploySha };
}

export function landingAnalyticsConsentState(
  cookieHeader: string,
): LandingAnalyticsConsentState {
  const values = cookieHeader.split(";").flatMap((part) => {
    const [name, ...valueParts] = part.trim().split("=");
    return name === landingAnalyticsConsentCookie ? [valueParts.join("=")] : [];
  });
  if (values.length !== 1) return "unset";
  const [value] = values;
  return value === "granted" || value === "denied" ? value : "unset";
}

export function hasLandingAnalyticsConsent(cookieHeader: string): boolean {
  return landingAnalyticsConsentState(cookieHeader) === "granted";
}

export function buildLandingAnalyticsConsentCookie(
  choice: Exclude<LandingAnalyticsConsentState, "unset">,
  secure: boolean,
  hostname?: string,
): string {
  const normalizedHostname = hostname?.trim().toLowerCase().replace(/\.$/, "");
  const sharedDomain = normalizedHostname === "dronejob.ai"
    || normalizedHostname?.endsWith(".dronejob.ai")
    ? ".dronejob.ai"
    : null;
  return [
    `${landingAnalyticsConsentCookie}=${choice}`,
    "Path=/",
    "Max-Age=15552000",
    "SameSite=Lax",
    ...(sharedDomain ? [`Domain=${sharedDomain}`] : []),
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

export function buildLandingAnalyticsConsentHostDeletionCookie(secure: boolean): string {
  return [
    `${landingAnalyticsConsentCookie}=`,
    "Path=/",
    "Max-Age=0",
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

function localeFromSegment(segment: string | undefined): LandingAnalyticsLocale | null {
  if (segment === "en" || segment === "es" || segment === "pt-BR") return segment;
  return null;
}

/**
 * Reduces every URL to a reviewed low-cardinality route key. Dynamic section
 * names, profile slugs, query strings, and fragments never leave the browser.
 */
export function landingPageViewDimensions(pathname: string): LandingPageViewDimensions {
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? "/";
  const segments = pathOnly.split("/").filter(Boolean);
  const locale = localeFromSegment(segments[0]);
  const routeSegments = locale ? segments.slice(1) : segments;

  if (routeSegments[0] === "p") {
    return { routeKey: "public_profile", locale: "not_applicable" };
  }
  if (locale) {
    if (routeSegments.length === 0) return { routeKey: "home", locale };
    const [parent, child] = routeSegments;
    if (parent === "join" && child === "pilot") return { routeKey: "join_pilot", locale };
    if (parent && legalRoutes.has(parent)) return { routeKey: `legal_${parent.replaceAll("-", "_")}`, locale };
    if (parent && localizedRoutes.has(parent)) {
      if (parent === "missions" && child === "post") return { routeKey: "missions_post", locale };
      return { routeKey: child ? `${parent}_detail` : parent, locale };
    }
    return { routeKey: "localized_other", locale };
  }

  const [parent, child] = routeSegments;
  if (!parent) return { routeKey: "home", locale: "not_applicable" };
  if (legalRoutes.has(parent)) {
    return { routeKey: `legal_${parent.replaceAll("-", "_")}`, locale: "not_applicable" };
  }
  if (parent === "engage" && child === "verify") {
    return { routeKey: "engagement_verification", locale: "not_applicable" };
  }
  if (parent === "pilots") {
    return { routeKey: child ? "pilot_directory_detail" : "pilot_directory", locale: "not_applicable" };
  }
  if (parent === "missions") {
    return { routeKey: "mission_directory", locale: "not_applicable" };
  }
  return { routeKey: "utility_other", locale: "not_applicable" };
}

type LandingVisitorStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function randomKey(prefix: "lav" | "lpe", cryptoImpl: Crypto): string {
  return `${prefix}:${cryptoImpl.randomUUID()}`;
}

function resolveLandingAnonymousVisitorId(
  storage: LandingVisitorStorage,
  cryptoImpl: Crypto,
): string {
  const storageKey = "dj:posthog-product-anonymous-visitor-v1";
  const raw = storage.getItem(storageKey);
  if (raw && raw.length <= 64 && /^lav:[0-9a-f-]{36}$/i.test(raw)) {
    return raw;
  }
  const id = randomKey("lav", cryptoImpl);
  storage.setItem(storageKey, id);
  return id;
}

export function clearLandingProductAnalyticsIdentity(storage: LandingVisitorStorage): void {
  try {
    storage.removeItem("dj:posthog-product-anonymous-visitor-v1");
  } catch {
    // Telemetry cleanup must never break the public product experience.
  }
}

export async function sendLandingPageView(input: Readonly<{
  config: LandingProductAnalyticsConfig | null;
  pathname: string;
  cookieHeader: string;
  visitorStorage: LandingVisitorStorage;
  fetchImpl?: typeof fetch;
  cryptoImpl?: Crypto;
  now?: number;
  signal?: AbortSignal;
}>): Promise<boolean> {
  if (
    !input.config
    || !landingAnalyticsEnvironmentSet.has(input.config.environment)
    || !hasLandingAnalyticsConsent(input.cookieHeader)
  ) return false;
  try {
    const fetchImpl = input.fetchImpl ?? fetch;
    const cryptoImpl = input.cryptoImpl ?? crypto;
    const distinctId = resolveLandingAnonymousVisitorId(
      input.visitorStorage,
      cryptoImpl,
    );
    const dimensions = landingPageViewDimensions(input.pathname);
    const response = await fetchImpl(`${input.config.host}/capture/`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "omit",
      keepalive: true,
      referrerPolicy: "no-referrer",
      signal: input.signal,
      body: JSON.stringify({
        api_key: input.config.projectToken,
        event: landingPageViewEventName,
        properties: {
          distinct_id: distinctId,
          $insert_id: randomKey("lpe", cryptoImpl),
          $process_person_profile: false,
          $geoip_disable: true,
          event_version: "v1",
          environment: input.config.environment,
          data_origin: "application",
          deploy_sha: input.config.deploySha,
          surface: "landing_page",
          route_key: dimensions.routeKey,
          locale: dimensions.locale,
        },
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
}
